export type InboxCategoryKey =
  | 'ALL_EXPERIENCES'
  | 'PRIVATE_BALLOON'
  | 'SHARED_BALLOON'
  | 'BUSH_LUNCH'
  | 'CONCIERGE_VIP'
  | 'TRANSFERS'
  | 'LODGE_CAMP'
  | 'PAYMENTS'
  | 'OPERATIONS'
  | 'GENERAL_INQUIRY'
  | 'NEEDS_REVIEW'
  | 'SPAM'
  | 'CHARTER'
  | 'SCHEDULED'
  | 'CONCIERGE'
  | 'NON_FLIGHT'
  | 'UNCERTAIN';

export type InboxCategoryConfig = {
  key: string;
  label: string;
  legacyAliases: string[];
  icon: string;
  tone: string;
  sortOrder: number;
  isOperational: boolean;
  visibleByDefault: boolean;
  countGroup: string;
};

const DEFAULT_INBOX_CATEGORIES: InboxCategoryConfig[] = [
  { key: 'ALL_EXPERIENCES', label: 'All Experiences', legacyAliases: [], icon: 'inbox', tone: 'ALL_EXPERIENCES', sortOrder: 0, isOperational: true, visibleByDefault: true, countGroup: 'ALL_EXPERIENCES' },
  { key: 'PRIVATE_BALLOON', label: 'Private Balloon', legacyAliases: ['CHARTER'], icon: 'balloon', tone: 'PRIVATE_BALLOON', sortOrder: 10, isOperational: true, visibleByDefault: true, countGroup: 'PRIVATE_BALLOON' },
  { key: 'SHARED_BALLOON', label: 'Shared Balloon', legacyAliases: ['SCHEDULED'], icon: 'circle-group', tone: 'SHARED_BALLOON', sortOrder: 20, isOperational: true, visibleByDefault: true, countGroup: 'SHARED_BALLOON' },
  { key: 'BUSH_LUNCH', label: 'Bush Lunch', legacyAliases: [], icon: 'basket', tone: 'BUSH_LUNCH', sortOrder: 30, isOperational: true, visibleByDefault: true, countGroup: 'BUSH_LUNCH' },
  { key: 'CONCIERGE_VIP', label: 'Concierge / VIP', legacyAliases: ['CONCIERGE'], icon: 'crown', tone: 'CONCIERGE_VIP', sortOrder: 40, isOperational: true, visibleByDefault: true, countGroup: 'CONCIERGE_VIP' },
  { key: 'TRANSFERS', label: 'Transfers', legacyAliases: [], icon: 'car', tone: 'TRANSFERS', sortOrder: 50, isOperational: true, visibleByDefault: false, countGroup: 'OPERATIONS' },
  { key: 'LODGE_CAMP', label: 'Lodge / Camp', legacyAliases: [], icon: 'home', tone: 'LODGE_CAMP', sortOrder: 60, isOperational: true, visibleByDefault: false, countGroup: 'OPERATIONS' },
  { key: 'PAYMENTS', label: 'Payments', legacyAliases: [], icon: 'receipt', tone: 'PAYMENTS', sortOrder: 70, isOperational: true, visibleByDefault: false, countGroup: 'OPERATIONS' },
  { key: 'OPERATIONS', label: 'Operations', legacyAliases: [], icon: 'clipboard', tone: 'OPERATIONS', sortOrder: 80, isOperational: true, visibleByDefault: true, countGroup: 'OPERATIONS' },
  { key: 'GENERAL_INQUIRY', label: 'General Inquiry', legacyAliases: ['NON_FLIGHT'], icon: 'help', tone: 'GENERAL_INQUIRY', sortOrder: 90, isOperational: false, visibleByDefault: true, countGroup: 'GENERAL_INQUIRY' },
  { key: 'NEEDS_REVIEW', label: 'Needs Review', legacyAliases: ['UNCERTAIN'], icon: 'search', tone: 'NEEDS_REVIEW', sortOrder: 100, isOperational: true, visibleByDefault: true, countGroup: 'NEEDS_REVIEW' },
  { key: 'SPAM', label: 'Spam', legacyAliases: ['SPAM'], icon: 'shield-x', tone: 'SPAM', sortOrder: 110, isOperational: false, visibleByDefault: true, countGroup: 'SPAM' },
];

let categoryConfigCache: InboxCategoryConfig[] = DEFAULT_INBOX_CATEGORIES.map((entry) => ({
  ...entry,
  legacyAliases: [...entry.legacyAliases],
}));

const normalizeKey = (value: unknown): string =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

const normalizeAliases = (value: unknown): string[] =>
  Array.from(new Set((Array.isArray(value) ? value : []).map((entry) => normalizeKey(entry)).filter(Boolean)));

const COMPACT_OPERATION_KEYS = new Set([
  'TRANSFERS',
  'LODGE_CAMP',
  'PAYMENTS',
]);

const FORCE_STANDALONE_CATEGORY_KEYS = new Set([
  'BUSH_LUNCH',
]);

const normalizeEntry = (raw: any, fallback?: InboxCategoryConfig): InboxCategoryConfig | null => {
  const key = normalizeKey(raw?.key || fallback?.key);
  if (!key) return null;
  return {
    key,
    label: String(raw?.label || fallback?.label || key.replace(/_/g, ' ')).trim(),
    legacyAliases: Array.from(new Set([key, ...normalizeAliases(raw?.legacyAliases), ...normalizeAliases(fallback?.legacyAliases)])),
    icon: String(raw?.icon || fallback?.icon || 'inbox').trim() || 'inbox',
    tone: String(raw?.tone || fallback?.tone || key).trim() || key,
    sortOrder: Number.isFinite(Number(raw?.sortOrder)) ? Number(raw.sortOrder) : Number(fallback?.sortOrder || 999),
    isOperational: typeof raw?.isOperational === 'boolean' ? raw.isOperational : fallback?.isOperational !== false,
    visibleByDefault: FORCE_STANDALONE_CATEGORY_KEYS.has(key)
      ? true
      : COMPACT_OPERATION_KEYS.has(key)
      ? false
      : typeof raw?.visibleByDefault === 'boolean'
        ? raw.visibleByDefault
        : fallback?.visibleByDefault !== false,
    countGroup: FORCE_STANDALONE_CATEGORY_KEYS.has(key)
      ? key
      : COMPACT_OPERATION_KEYS.has(key)
      ? 'OPERATIONS'
      : normalizeKey(raw?.countGroup || fallback?.countGroup || key) || key,
  };
};

export const setInboxCategoryConfigCache = (categories: unknown): InboxCategoryConfig[] => {
  const saved = Array.isArray(categories) ? categories : [];
  const byKey = new Map<string, InboxCategoryConfig>();
  for (const fallback of DEFAULT_INBOX_CATEGORIES) {
    const match = saved.find((entry: any) => normalizeKey((entry as any)?.key) === fallback.key);
    const normalized = normalizeEntry(match || fallback, fallback);
    if (normalized) byKey.set(normalized.key, normalized);
  }
  for (const raw of saved) {
    const normalized = normalizeEntry(raw);
    if (normalized && !byKey.has(normalized.key)) byKey.set(normalized.key, normalized);
  }
  categoryConfigCache = Array.from(byKey.values()).sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));
  return categoryConfigCache;
};

export const getInboxCategoryConfigCache = (): InboxCategoryConfig[] => categoryConfigCache;

export const getInboxCategoryConfigFor = (value: unknown): InboxCategoryConfig => {
  const normalized = normalizeKey(value || 'UNCERTAIN');
  const matched = categoryConfigCache.find((entry) => entry.key !== 'ALL_EXPERIENCES' && (entry.key === normalized || entry.legacyAliases.includes(normalized)));
  return matched || categoryConfigCache.find((entry) => entry.key === 'NEEDS_REVIEW') || DEFAULT_INBOX_CATEGORIES[10];
};

export const getVisibleInboxCategoryConfigs = (): InboxCategoryConfig[] =>
  categoryConfigCache.filter((entry) => entry.visibleByDefault).sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));
